'use client';

import { useEffect, useState } from 'react';

import { useSocket } from '@/hooks/useSocket';

/**
 * Expected shape of live updates received from the sports WebSocket.
 */
interface LiveUpdate {
  metadataGameId: string;
  gameId?: string | number;
  leagueAbbreviation: string;
  score: string;
  period: string;
  elapsed: string | null;
  live: boolean;
  ended: boolean;
}

/**
 * Hook to manage live sports data connection via WebSocket.
 * It listens to 'sports' namespace updates and maintains a dictionary
 * of game statistics keyed by the unique `metadataGameId`.
 */
export const useLiveSportsSocket = () => {
  // Store live updates grouped by game ID for quick lookups in UI components
  const [liveData, setLiveData] = useState<Record<string, Partial<LiveUpdate>>>({});

  // Initialize socket connection for the 'sports' namespace
  const { isConnected, send } = useSocket('sports', (data) => {
    try {
      // Handle both raw string payloads and pre-parsed JSON objects
      const message = typeof data === 'string' ? JSON.parse(data) : data;
      const targetId = message.gameId || message.metadataGameId;

      // Only process messages that contain a game ID
      if (targetId) {
        setLiveData((prev) => ({
          ...prev,
          // Merge new data over existing data to keep partial updates intact
          [targetId.toString()]: {
            ...prev[targetId.toString()],
            ...message,
          },
        }));
      }
    } catch (error) {
      // Gracefully handle parsing errors without crashing the socket listener
    }
  });

  // Handle post-connection initialization
  useEffect(() => {
    if (isConnected) {
      // Send an initial ping or handshake message if required by the backend to acknowledge connection
      send({ type: 'ping', timestamp: new Date().toISOString() });
    }
  }, [isConnected, send]);

  return {
    liveData,
    isConnected,
  };
};
